/**
 * @file region-localization.ts
 *
 * Localizes the displayed continent and country names in a {@link ShopContinent}
 * tree to the active admin locale using the platform's built-in CLDR data via
 * `Intl.DisplayNames` (the same API the language switcher uses) — no extra
 * dependency, no bundled data. Country codes are ISO 3166-1 alpha-2 and resolve
 * directly; WooCommerce continent codes are mapped to UN M49 region codes first
 * (a continent is only resolved through that map, never by passing its raw
 * two-letter code to `Intl.DisplayNames`, since codes like `NA`/`AS`/`SA` collide
 * with real country codes — Namibia, American Samoa, Saudi Arabia).
 *
 * Only the display `name`s change — every `code` (and so the saved selection) is
 * untouched, and any name the platform can't resolve keeps its API value.
 *
 * State/province (ISO 3166-2) names are intentionally NOT localized: `Intl`
 * has no subdivision support, and CLDR ships subdivision translations for English
 * only (de/fr etc. carry ~3 entries), so they keep their API value.
 */

import type { ShopContinent } from '../api/shop-types';

/** WooCommerce continent codes → UN M49 region codes that `Intl.DisplayNames` knows. */
const CONTINENT_TO_M49: Record<string, string> = {
  AF: '002', // Africa
  AN: '010', // Antarctica
  AS: '142', // Asia
  EU: '150', // Europe
  NA: '003', // North America
  OC: '009', // Oceania
  SA: '005', // South America
};

/** Region `DisplayNames` instances are cached per locale (constructing isn't free). */
const regionDisplayNamesCache = new Map<string, Intl.DisplayNames | null>();

/**
 * Returns the cached region `DisplayNames` for a locale, or `null` if the platform
 * can't build one (older runtime / unsupported locale).
 *
 * @param locale - The active BCP-47 locale.
 *
 * @returns The `Intl.DisplayNames` instance, or `null`.
 */
function getRegionDisplayNames(locale: string): Intl.DisplayNames | null {
  if (!regionDisplayNamesCache.has(locale)) {
    try {
      regionDisplayNamesCache.set(
        locale,
        new Intl.DisplayNames([locale], { type: 'region', fallback: 'none' }),
      );
    } catch {
      regionDisplayNamesCache.set(locale, null);
    }
  }
  return regionDisplayNamesCache.get(locale) ?? null;
}

/**
 * Resolves a region code to its localized name, falling back to `fallback` when the
 * code is unknown or the platform can't resolve it.
 *
 * @param display - The locale's region `DisplayNames` (or `null`).
 * @param code - A UN M49 or ISO 3166-1 alpha-2 region code.
 * @param fallback - The name to use when the code can't be resolved.
 *
 * @returns The localized name, or `fallback`.
 */
function resolveRegion(
  display: Intl.DisplayNames | null,
  code: string,
  fallback: string,
): string {
  if (!display) {
    return fallback;
  }
  try {
    return display.of(code) ?? fallback;
  } catch {
    return fallback;
  }
}

/**
 * Returns a copy of the continent tree with continent and country `name`s localized
 * to `locale`. State names and every code are left unchanged.
 *
 * @param continents - The region tree from the API.
 * @param locale - The active BCP-47 locale.
 *
 * @returns A localized copy of the tree.
 */
export function localizeContinents(
  continents: ShopContinent[],
  locale: string,
): ShopContinent[] {
  const display = getRegionDisplayNames(locale);
  return continents.map((continent) => {
    const m49 = CONTINENT_TO_M49[continent.code];
    return {
      ...continent,
      name: m49 ? resolveRegion(display, m49, continent.name) : continent.name,
      countries: continent.countries.map((country) => ({
        ...country,
        name: resolveRegion(display, country.code, country.name),
      })),
    };
  });
}
